import React, { useEffect, useState } from 'react'
import { Modal, Button } from './ui'
import { LoadingSpinner } from './LoadingSpinner'
import { Volunteer } from '../data/types'
import { API_BASE_URL, LoadingSpinnerMeta } from '../data'
import { VolunteerProfileCard } from './VolunteerProfileCard'
import { ProfileMeta } from '../data'

type Props = {
    volunteerId: number | null
    isOpen: boolean
    onClose: () => void
}

export const VolunteerProfileModal = (props: Props) => {
    const { volunteerId, isOpen, onClose } = props
    const [volunteer, setVolunteer] = useState<Volunteer | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!isOpen || !volunteerId) {
            setVolunteer(null)
            setError(null)
            return
        }

        const fetchVolunteer = async () => {
            setLoading(true)
            setError(null)
            try {
                const response = await fetch(`${API_BASE_URL}/volunteer/${volunteerId}`, {
                    credentials: 'include'
                })
                if (!response.ok) {
                    throw new Error(`Failed to fetch volunteer: ${response.status}`)
                }
                const result = await response.json()
                let data = result.value || result

                // Handle double-nested arrays
                if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
                    data = data[0]
                }

                setVolunteer(Array.isArray(data) ? data[0] : data)
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred while fetching volunteer data')
            } finally {
                setLoading(false)
            }
        }

        fetchVolunteer()
    }, [volunteerId, isOpen])

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={volunteer ? `${volunteer.first_name} ${volunteer.last_name}` : 'Volunteer Profile'}
            showCloseButton={true}
            maxWidth="5xl"
            footer={
                <div className="flex justify-end">
                    <Button onClick={onClose} variant="outline" color="neutral">
                        Close
                    </Button>
                </div>
            }
        >
            <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                {loading && (
                    <div className="flex justify-center items-center min-h-[200px]">
                        <LoadingSpinner meta={LoadingSpinnerMeta} />
                    </div>
                )}

                {!loading && error && (
                    <div className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                        <p className="font-bold text-lg mb-2">Error</p>
                        <p>{error}</p>
                    </div>
                )}

                {!loading && !error && volunteer && (
                    <VolunteerProfileCard
                        volunteer={volunteer}
                        meta={ProfileMeta}
                        adminSide={true}
                    />
                )}
            </div>
        </Modal>
    )
}
